/**
 * ===============================
 * Date & Time Utilities
 * ===============================
 * Backend → UTC
 * Frontend → Local Time (user/admin timezone)
 */

const DEFAULT_LOCALE = "en-IN";

/**
 * Convert UTC datetime → Local time (HH:mm AM/PM)
 * Example:
 *  "2026-01-01T04:00:00Z" → "09:30 AM" (IST)
 */
export const utcToLocalTime = (
  utcString,
  timeZone,
  locale = DEFAULT_LOCALE
) => {
  if (!utcString) return "--";

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(utcString));
};

/**
 * Convert UTC datetime → Local date + time
 * Example:
 *  "2026-01-01T04:00:00Z" → "01 Jan 2026, 09:30 AM"
 */
export const utcToLocalDateTime = (
  utcString,
  timeZone,
  locale = DEFAULT_LOCALE
) => {
  if (!utcString) return "--";

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(utcString));
};

/**
 * Convert UTC datetime → Local date only
 * Example:
 *  "2026-01-01T04:00:00Z" → "01 Jan 2026"
 */
export const utcToLocalDate = (
  utcString,
  timeZone,
  locale = DEFAULT_LOCALE
) => {
  if (!utcString) return "--";

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(utcString));
};

/**
 * Combine local date + time → ISO string with timezone offset
 * Used before sending to backend
 *
 * Example:
 *  date = "21 Jan 2026"
 *  time = "17:00"
 *  → "2026-01-21T17:00:00+05:30" (for IST)
 */
export const buildLocalDateTime = (dayDate, time) => {
  if (!dayDate || !time) return null;

  // Convert "21 Jan 2026" -> "2026-01-21"
  const [day, monthStr, year] = dayDate.split(" ");
  const month = new Date(`${monthStr} 1, 2000`).getMonth(); // 0-based month
  const dateObj = new Date(year, month, day);

  // Split hours and minutes
  const [hours, minutes] = time.split(":").map(Number);
  dateObj.setHours(hours, minutes, 0, 0);

  // Get timezone offset in ±HH:MM format
  const offsetMinutes = -dateObj.getTimezoneOffset(); // JS offset is opposite sign
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absOffset = Math.abs(offsetMinutes);
  const offsetHrs = String(Math.floor(absOffset / 60)).padStart(2, "0");
  const offsetMin = String(absOffset % 60).padStart(2, "0");
  const offsetStr = `${sign}${offsetHrs}:${offsetMin}`;

  return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2,"0")}-${String(dateObj.getDate()).padStart(2,"0")}T${String(dateObj.getHours()).padStart(2,"0")}:${String(dateObj.getMinutes()).padStart(2,"0")}:00${offsetStr}`;
};


/**
 * Extract local time (HH:mm) from UTC datetime
 * Useful while editing sessions
 *
 * Example:
 *  "2026-01-01T04:00:00Z" → "09:30" (IST)
 */
export const extractLocalTime = (utcString, timeZone) => {
  if (!utcString) return "";

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return formatter.format(new Date(utcString));
};

/**
 * Format date into: "02 Jan 2026, SATURDAY"
 * Used for headers / accordions
 */
export const formatDayDate = (
  utcString,
  timeZone,
  locale = DEFAULT_LOCALE
) => {
  if (!utcString) return "";

  const date = new Date(utcString);

  const formattedDate = new Intl.DateTimeFormat(locale, {
    timeZone,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);

  const weekday = new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: "long",
  })
    .format(date)
    .toUpperCase();

  return `${formattedDate}, ${weekday}`;
};

/**
 * Convert UTC → Readable local date
 * Example:
 *  "Wed, 21 Jan 2026"
 */
export const utcToLocalReadableDate = (
  utcString,
  timeZone,
  locale = DEFAULT_LOCALE
) => {
  if (!utcString) return "--";

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(utcString));
};
